import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import * as Sentry from '@sentry/react'

import { AuthService } from '../services/auth.service'
import { USER_ROLE } from '../services/enums/lutien.enum'
import { LutienApi } from '../services/LutienApi'
import {
  IAuthState,
  ICredentials,
  ICredentialsOTP,
  TEmbeddedSession,
  TError,
  TLoginOTP,
  TUser
} from '../types/auth.types'

const initialState: IAuthState = {
  logoutLoading: false,
  logoutSuccess: false,
  logoutError: undefined,

  loginPasswordLoading: false,
  loginPasswordSuccess: false,
  loginPasswordError: undefined,

  loginEmbeddedLoading: false,
  loginEmbeddedSuccess: false,
  loginEmbeddedError: undefined,

  loginFederatedLoading: false,
  loginFederatedSuccess: false,
  loginFederatedError: undefined,

  loginOtpLoading: false,
  loginOtpSuccess: false,
  loginOtpError: undefined,
  loginOtpId: undefined,

  loginOtpChallengerLoading: false,
  loginOtpChallengerSuccess: false,
  loginOtpChallengerError: undefined,

  userInfoLoading: false,
  userInfoSuccess: false,
  userInfoError: undefined,
  userInfo: undefined,

  idUser: '',
  isLogged: false
}

const errorsToIgnore = [
  'Incorrect username or password.',
  'UserMigration failed with error User does not exist..',
  'UserMigration failed with error Incorrect username or password..',
  'User does not exist.',
  'O código é inválido. Tente novamente.',
  'Email e/ou senha inválidos',
  'Acesso restrito a funcionários da Tarken'
]

// Password login thunk
export const loginPassword = createAsyncThunk(
  'authenticator/loginPassword',
  async (credentials: ICredentials) => {
    const response = await AuthService.loginPassword(credentials.email, credentials.password)
    if (!response.signInUserSession) {
      throw new Error('Email e/ou senha inválidos')
    }

    // Get current session to extract token
    const session = await AuthService.getCurrentSession()
    const accessToken = session?.getAccessToken()?.getJwtToken() || ''

    // Get user info from Cognito first
    const cognitoUserInfo = await AuthService.getUserInfo(accessToken)
    let userInfo = cognitoUserInfo

    // Try to get complete user info from Lutien API if available
    try {
      const lutienUserInfo = await LutienApi.getUserInfo(accessToken)

      userInfo = {
        ...cognitoUserInfo,
        ...lutienUserInfo,
        role: lutienUserInfo.role || cognitoUserInfo.role
      }
    } catch (error: any) {
      // If Lutien API fails (500 error), use Cognito info
      console.warn('Lutien API /me endpoint failed, using Cognito user info', error)
    }

    // Check if user is staff
    if (userInfo.role !== USER_ROLE.ROLE_STAFF) {
      throw new Error('Acesso restrito a funcionários da Tarken')
    }

    return {
      userInfo: {
        id: userInfo.id,
        email: userInfo.email,
        name: userInfo.name,
        lastName: userInfo.lastName,
        phoneNumber: userInfo.phoneNumber,
        role: userInfo.role
      },
      isLogged: credentials.isLogged || false
    }
  }
)

// OTP login thunk
export const loginOTP = createAsyncThunk(
  'authenticator/loginOTP',
  async (credentials: ICredentialsOTP) => {
    const response = await AuthService.loginOTP(credentials.phone, credentials.email)
    return { id: response?.id }
  }
)

// OTP challenge thunk
export const loginOTPChallenge = createAsyncThunk(
  'authenticator/loginOTPChallenge',
  async ({ phoneEmail, code }: TLoginOTP) => {
    const response = await AuthService.loginOTPChallenge(phoneEmail, code)
    if (!response.signInUserSession) {
      throw new Error('O código é inválido. Tente novamente.')
    }

    // Get current session to extract token
    const session = await AuthService.getCurrentSession()
    const accessToken = session?.getAccessToken()?.getJwtToken() || ''

    // Get user info from Cognito first
    const cognitoUserInfo = await AuthService.getUserInfo(accessToken)
    let userInfo = cognitoUserInfo

    // Try to get complete user info from Lutien API if available
    try {
      const lutienUserInfo = await LutienApi.getUserInfo(accessToken)

      userInfo = {
        ...cognitoUserInfo,
        ...lutienUserInfo,
        role: lutienUserInfo.role || cognitoUserInfo.role
      }
    } catch (error: any) {
      // If Lutien API fails (500 error), use Cognito info
      console.warn('Lutien API /me endpoint failed, using Cognito user info', error)
    }

    // Check if user is staff
    if (userInfo.role !== USER_ROLE.ROLE_STAFF) {
      throw new Error('Acesso restrito a funcionários da Tarken')
    }

    return {
      userInfo: {
        id: userInfo.id,
        email: userInfo.email,
        name: userInfo.name,
        lastName: userInfo.lastName,
        phoneNumber: userInfo.phoneNumber,
        role: userInfo.role
      }
    }
  }
)

// Embedded auth thunk
export const loginEmbedded = createAsyncThunk(
  'authenticator/loginEmbedded',
  async (session: TEmbeddedSession) => {
    await AuthService.loginEmbedded(session)

    // Get current session to extract token
    const currentSession = await AuthService.getCurrentSession()
    const accessToken = currentSession?.getAccessToken()?.getJwtToken() || ''

    // Get user info from Cognito first
    const cognitoUserInfo = await AuthService.getUserInfo(accessToken)
    let userInfo = cognitoUserInfo

    // Try to get complete user info from Lutien API if available
    try {
      const lutienUserInfo = await LutienApi.getUserInfo(accessToken)

      userInfo = {
        ...cognitoUserInfo,
        ...lutienUserInfo,
        role: lutienUserInfo.role || cognitoUserInfo.role
      }
    } catch (error: any) {
      // If Lutien API fails (500 error), use Cognito info
      console.warn('Lutien API /me endpoint failed, using Cognito user info', error)
    }

    // Check if user is staff
    if (userInfo.role !== USER_ROLE.ROLE_STAFF) {
      throw new Error('Acesso restrito a funcionários da Tarken')
    }

    return {
      userInfo: {
        id: userInfo.id,
        email: userInfo.email,
        name: userInfo.name,
        lastName: userInfo.lastName,
        phoneNumber: userInfo.phoneNumber,
        role: userInfo.role
      }
    }
  }
)

// Federated auth thunk
export const loginFederated = createAsyncThunk(
  'authenticator/loginFederated',
  async (code: string) => {
    await AuthService.loginFederated(code)

    // Get current session to extract token
    const session = await AuthService.getCurrentSession()
    const accessToken = session?.getAccessToken()?.getJwtToken() || ''

    // Get user info from Cognito first
    const cognitoUserInfo = await AuthService.getUserInfo(accessToken)
    let userInfo = cognitoUserInfo

    // Try to get complete user info from Lutien API if available
    try {
      const lutienUserInfo = await LutienApi.getUserInfo(accessToken)

      userInfo = {
        ...cognitoUserInfo,
        ...lutienUserInfo,
        role: lutienUserInfo.role || cognitoUserInfo.role
      }
    } catch (error: any) {
      // If Lutien API fails (500 error), use Cognito info
      console.warn('Lutien API /me endpoint failed, using Cognito user info', error)
    }

    // Check if user is staff
    if (userInfo.role !== USER_ROLE.ROLE_STAFF) {
      throw new Error('Acesso restrito a funcionários da Tarken')
    }

    return {
      userInfo: {
        id: userInfo.id,
        email: userInfo.email,
        name: userInfo.name,
        lastName: userInfo.lastName,
        phoneNumber: userInfo.phoneNumber,
        role: userInfo.role
      }
    }
  }
)

// Logout thunk
export const logout = createAsyncThunk('authenticator/logout', async () => {
  await AuthService.logout()
})

// Get user info thunk
export const getUserInfo = createAsyncThunk('authenticator/getUserInfo', async () => {
  const session = await AuthService.getCurrentSession()
  const accessToken = session?.getAccessToken()?.getJwtToken() || ''

  // Get user info from Cognito first
  const cognitoUserInfo = await AuthService.getUserInfo(accessToken)
  let userInfo = cognitoUserInfo

  // Try to get complete user info from Lutien API if available
  try {
    const lutienUserInfo = await LutienApi.getUserInfo(accessToken)

    userInfo = {
      ...cognitoUserInfo,
      ...lutienUserInfo,
      role: lutienUserInfo.role || cognitoUserInfo.role
    }
  } catch (error: any) {
    // If Lutien API fails (500 error), use Cognito info
    console.warn('Lutien API /me endpoint failed, using Cognito user info', error)
  }

  // Check if user is staff
  if (userInfo.role !== USER_ROLE.ROLE_STAFF) {
    throw new Error('Acesso restrito a funcionários da Tarken')
  }

  return {
    id: userInfo.id,
    email: userInfo.email,
    name: userInfo.name,
    lastName: userInfo.lastName,
    phoneNumber: userInfo.phoneNumber,
    role: userInfo.role
  }
})

// Check if user is authenticated
export const checkAuth = createAsyncThunk('authenticator/checkAuth', async () => {
  const session = await AuthService.getCurrentSession()
  if (!session) {
    throw new Error('No active session')
  }

  const accessToken = session.getAccessToken().getJwtToken()

  // Get user info from Cognito first
  const cognitoUserInfo = await AuthService.getUserInfo(accessToken)
  let userInfo = cognitoUserInfo

  // Try to get complete user info from Lutien API if available
  try {
    const lutienUserInfo = await LutienApi.getUserInfo(accessToken)

    userInfo = {
      ...cognitoUserInfo,
      ...lutienUserInfo,
      role: lutienUserInfo.role || cognitoUserInfo.role
    }
  } catch (error: any) {
    // If Lutien API fails (500 error), use Cognito info
    console.warn('Lutien API /me endpoint failed, using Cognito user info', error)
  }

  // Check if user is staff
  if (userInfo.role !== USER_ROLE.ROLE_STAFF) {
    throw new Error('Acesso restrito a funcionários da Tarken')
  }

  return {
    id: userInfo.id,
    email: userInfo.email,
    name: userInfo.name,
    lastName: userInfo.lastName,
    phoneNumber: userInfo.phoneNumber,
    role: userInfo.role
  }
})

export const authenticatorSlice = createSlice({
  name: 'authenticator',
  initialState,
  reducers: {
    setIsLogged: (state, action: PayloadAction<boolean>) => {
      state.isLogged = action.payload
    },
    clearAuthState: state => {
      return {
        ...initialState,
        idUser: state.idUser,
        isLogged: state.isLogged
      }
    }
  },
  extraReducers: builder => {
    // Password login
    builder
      .addCase(loginPassword.pending, state => {
        state.loginPasswordLoading = true
        state.loginPasswordSuccess = false
        state.loginPasswordError = undefined
      })
      .addCase(loginPassword.fulfilled, (state, action) => {
        state.loginPasswordLoading = false
        state.loginPasswordSuccess = true
        state.loginPasswordError = undefined
        state.userInfo = action.payload.userInfo
        state.idUser = action.payload.userInfo.id
        state.isLogged = true
        Sentry.setUser({ email: action.payload.userInfo.email, id: action.payload.userInfo.id })
      })
      .addCase(loginPassword.rejected, (state, action) => {
        state.loginPasswordLoading = false
        state.loginPasswordSuccess = false
        state.loginPasswordError = action.error.message || 'Login failed'
        if (!errorsToIgnore.includes(action.error.message || '')) {
          Sentry.captureException(action.error)
        }
      })

    // OTP login
    builder
      .addCase(loginOTP.pending, state => {
        state.loginOtpLoading = true
        state.loginOtpSuccess = false
        state.loginOtpError = undefined
        state.loginOtpId = undefined
      })
      .addCase(loginOTP.fulfilled, (state, action) => {
        state.loginOtpLoading = false
        state.loginOtpSuccess = true
        state.loginOtpError = undefined
        state.loginOtpId = action.payload.id
      })
      .addCase(loginOTP.rejected, (state, action) => {
        state.loginOtpLoading = false
        state.loginOtpSuccess = false
        state.loginOtpError = action.error.message || 'OTP request failed'
        state.loginOtpId = undefined
      })

    // OTP challenge
    builder
      .addCase(loginOTPChallenge.pending, state => {
        state.loginOtpChallengerLoading = true
        state.loginOtpChallengerSuccess = false
        state.loginOtpChallengerError = undefined
      })
      .addCase(loginOTPChallenge.fulfilled, (state, action) => {
        state.loginOtpChallengerLoading = false
        state.loginOtpChallengerSuccess = true
        state.loginOtpChallengerError = undefined
        state.userInfo = action.payload.userInfo
        state.idUser = action.payload.userInfo.id
        state.isLogged = true
        Sentry.setUser({ email: action.payload.userInfo.email, id: action.payload.userInfo.id })
      })
      .addCase(loginOTPChallenge.rejected, (state, action) => {
        state.loginOtpChallengerLoading = false
        state.loginOtpChallengerSuccess = false
        state.loginOtpChallengerError = action.error.message || 'Invalid code'
        if (!errorsToIgnore.includes(action.error.message || '')) {
          Sentry.captureException(action.error)
        }
      })

    // Embedded auth
    builder
      .addCase(loginEmbedded.pending, state => {
        state.loginEmbeddedLoading = true
        state.loginEmbeddedSuccess = false
        state.loginEmbeddedError = undefined
      })
      .addCase(loginEmbedded.fulfilled, (state, action) => {
        state.loginEmbeddedLoading = false
        state.loginEmbeddedSuccess = true
        state.loginEmbeddedError = undefined
        state.userInfo = action.payload.userInfo
        state.idUser = action.payload.userInfo.id
        state.isLogged = true
        Sentry.setUser({ email: action.payload.userInfo.email, id: action.payload.userInfo.id })
      })
      .addCase(loginEmbedded.rejected, (state, action) => {
        state.loginEmbeddedLoading = false
        state.loginEmbeddedSuccess = false
        state.loginEmbeddedError = action.error.message || 'Embedded auth failed'
        if (!errorsToIgnore.includes(action.error.message || '')) {
          Sentry.captureException(action.error)
        }
      })

    // Federated auth
    builder
      .addCase(loginFederated.pending, state => {
        state.loginFederatedLoading = true
        state.loginFederatedSuccess = false
        state.loginFederatedError = undefined
      })
      .addCase(loginFederated.fulfilled, (state, action) => {
        state.loginFederatedLoading = false
        state.loginFederatedSuccess = true
        state.loginFederatedError = undefined
        state.userInfo = action.payload.userInfo
        state.idUser = action.payload.userInfo.id
        state.isLogged = true
        Sentry.setUser({ email: action.payload.userInfo.email, id: action.payload.userInfo.id })
      })
      .addCase(loginFederated.rejected, (state, action) => {
        state.loginFederatedLoading = false
        state.loginFederatedSuccess = false
        state.loginFederatedError = action.error.message || 'Federated auth failed'
        if (!errorsToIgnore.includes(action.error.message || '')) {
          Sentry.captureException(action.error)
        }
      })

    // Logout
    builder
      .addCase(logout.pending, state => {
        state.logoutLoading = true
        state.logoutSuccess = false
        state.logoutError = undefined
      })
      .addCase(logout.fulfilled, state => {
        state.logoutLoading = false
        state.logoutSuccess = true
        state.logoutError = undefined
        state.isLogged = false
        state.userInfo = undefined
        state.idUser = ''
        Sentry.setUser(null)
      })
      .addCase(logout.rejected, (state, action) => {
        state.logoutLoading = false
        state.logoutSuccess = false
        state.logoutError = action.error.message || 'Logout failed'
        Sentry.captureException(action.error)
      })

    // Get user info
    builder
      .addCase(getUserInfo.pending, state => {
        state.userInfoLoading = true
        state.userInfoSuccess = false
        state.userInfoError = undefined
      })
      .addCase(getUserInfo.fulfilled, (state, action) => {
        state.userInfoLoading = false
        state.userInfoSuccess = true
        state.userInfoError = undefined
        state.userInfo = action.payload
        state.idUser = action.payload.id
        Sentry.setUser({ email: action.payload.email, id: action.payload.id })
      })
      .addCase(getUserInfo.rejected, (state, action) => {
        state.userInfoLoading = false
        state.userInfoSuccess = false
        state.userInfoError = action.error.message || 'Failed to get user info'
      })

    // Check auth
    builder
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.isLogged = true
        state.userInfo = action.payload
        state.idUser = action.payload.id
        Sentry.setUser({ email: action.payload.email, id: action.payload.id })
      })
      .addCase(checkAuth.rejected, state => {
        state.isLogged = false
        state.userInfo = undefined
        state.idUser = ''
      })
  }
})

export const { setIsLogged, clearAuthState } = authenticatorSlice.actions

export const authenticatorReducer = authenticatorSlice.reducer

// Backward compatibility exports
export const login = loginPassword
